/**
 * @file 自定义服务
 * @author fuqiang15
 */

import FormModel from './ApiFormModel';
import {omit, each, keys, get} from 'lodash';
import {purify} from '@baidu/ve-ria/utils/lang';

const ACCESS_LIMIT_KEYS = ['qps', 'qpd', 'qph', 'qpm', 'totalCount'];
export default class CustomServiceFormModel extends FormModel {
    formData = {
        // 基本信息
        name: '',
        protocol: null,
        requestMethod: null,
        contentType: null,
        sourceUrl: '',
        proxyUrl: '',
        encoding: '',
        authType: null,
        authInfo: '',
        signType: null,
        signLocation: null,
        signKey: null,
        signTemplate: null,
        // 和以前保持一致， 默认选否
        requireUid: false,
        // 返回示例说明
        responseExample: null,
        // 已添加参数
        parameters: [],
        // cors 域名白名单
        corsDomainWhiteList: '',

        // 访问控制信息
        qps: null,
        qpd: null,
        qph: null,
        qpm: null,
        totalCount: null,
        stability: null,
        retryCount: 0,
        timeout: 10,
        cacheExpire: 0
    };

    parameter = {
        name: null,
        required: false,
        location: null,
        defaultValue: null,
        complexBodyParam: false,
        type: null,
        description: null
    }

    protocolDatasource = this.getConstantDatasource('API_PROTOCOL');
    methodDatasource = this.getConstantDatasource('API_REQUEST_METHOD');
    methodMap = this.getConstantMap('API_REQUEST_METHOD');
    contentTypeDatasource = this.getConstantDatasource('API_CONTENT_TYPE');
    encodingDatasource = this.getConstantDatasource('API_ENCODING');
    authTypeDatasource = this.getConstantDatasource('API_AUTH_TYPE');
    signTypeDatasource = this.getConstantDatasource('API_SIGN_TYPE');
    locationDatasource = this.getConstantDatasource('HTTP_LOCATION');
    trueFalseDatasource = this.getConstantDatasource('TRUE_AND_FALSE');

    parameterTypeDatasource = [
        {value: 'String', label: 'String'},
        {value: 'Number', label: 'Number'},
        {value: 'Bool', label: 'Bool'}
    ];
    stabilityDatasource = [
        {value: '>99.97%', label: '> 99.97%'},
        {value: '>99%', label: '> 99%'},
        {value: '>98%', label: '> 98%'},
        {value: '>95%', label: '> 95%'},
        {value: '<95%', label: '< 95%'}
    ];

    defaultValueMap = {
        authType: 'authTypeDatasource',
        location: 'locationDatasource',
        protocol: 'protocolDatasource',
        encoding: 'encodingDatasource',
        signType: 'signTypeDatasource',
        type: 'parameterTypeDatasource',
        stability: 'stabilityDatasource',
        requestMethod: 'methodDatasource',
        signLocation: 'locationDatasource',
        contentType: 'contentTypeDatasource'
    };

    getDefaultValue(arr) {
        if (arr.length) {
            return get(arr[0], 'value', null);
        }
        return null;
    }

    prepareDefaultData() {
        keys(this.defaultValueMap).forEach(item => {
            const str = this.defaultValueMap[item];
            const target = this.parameter.hasOwnProperty(item) ? this.parameter : this.formData;

            target[item] = this.getDefaultValue(get(this, str, []));
        });
    }

    init() {
        super.init();

        if (this.pageType === 'create') {
            this.createRequester = this.api.appServiceCustomAPICreate;
            each(ACCESS_LIMIT_KEYS, key => {
                this.formData[key] = '-1';
            });
        }
        else if (this.pageType === 'edit') {
            this.updateRequester = this.api.appServiceCustomAPIUpdate;
            this.detailRequester = this.api.appServiceCustomAPIDetail;
        }
        else if (this.pageType === 'detail') {
            this.detailRequester = this.api.appServiceCustomAPIDetail;
        }

        this.prepareDefaultData();
    }

    adaptData(data) {
        if (data && data.parameters) {
            data.parameters = JSON.parse(data.parameters);

            let accessLimit = JSON.parse(data.accessLimit);
            each(ACCESS_LIMIT_KEYS, key => {
                data[key] = accessLimit[key] || '-1';
            });
        }
        return super.adaptData(data);
    }
    /**
     * @override
     */
    prepareData(data) {
        let accessLimit = {};
        each(ACCESS_LIMIT_KEYS, key => {
            if (data[key] !== '-1') {
                accessLimit[key] = data[key];
            }
        });
        // 去除多余 space 的影响
        data.corsDomainWhiteList = data.corsDomainWhiteList ? data.corsDomainWhiteList.trim() : '';

        data.accessLimit = JSON.stringify(accessLimit);
        data.parameters = JSON.stringify(data.parameters);
        data.proxyUrl = this.removeStartSlash(data.proxyUrl);

        if (this.pageType === 'edit') {
            data.id = this.id;
        }
        data.serviceId = this.serviceId;
        return {
            ...purify(omit(data, ACCESS_LIMIT_KEYS)),
            corsDomainWhiteList: data.corsDomainWhiteList
        };
    }

    setCrumbData(appId, serviceId) {
        super.setCrumbData(appId, serviceId);

        let actionText = {
            create: '创建',
            edit: '编辑',
            detail: '查看'
        }[this.pageType];
        this.crumbData[2].text = `${actionText}API`;

        this.setProxyUrlPrefix(appId, serviceId);
    }

    async setProxyUrlPrefix(appId, serviceId) {
        if (!appId) {
            let data = await this.api.appServiceDetail({id: serviceId});
            appId = data.appId;
        }
        this.proxyUrlPrefix = `${this.proxyUrlPrefix}`;
    }

    getApiTestUrl(apiId = this.id, action) {
        let apiPrefix = `/custom/api/${apiId}`;
        if (this.isAdmin) {
            return `${this.servicePrefix}${apiPrefix}/sandbox/${action}`;
        }
        return `${this.appPrefix}${this.servicePrefix}${apiPrefix}/sandbox/${action}`;
    }

    getServiceListUrl() {
        if (this.isAdmin) {
            return '/service/all/list';
        }
        return this.crumbData[0].to;
    }
}
